import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiChevronLeft, FiChevronRight, FiCheck, FiArrowLeft,
  FiBookOpen, FiUsers, FiSearch, FiAlertCircle
} from 'react-icons/fi';
import { FaWhatsapp, FaMale, FaFemale, FaChild, FaUserGraduate } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { recitativoService, bibliaService, congregacaoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { exportarContatosWhatsAppHTML } from '../../utils/exportHelpers';
import './NovoRecitativo.css';

const TURMAS = [
  { id: 1, label: '1ª turma', faixa: '3 a 6 anos', icon: FaChild, cor: '#f59e0b', desc: 'Crianças pequenas (mesmo versículo para todos)' },
  { id: 2, label: '2ª turma', faixa: '7 a 11 anos', icon: FaChild, cor: '#3b82f6', desc: 'Crianças (versículos sequenciais)' },
  { id: 3, label: '3ª turma', faixa: '12 anos ou mais', icon: FaUserGraduate, cor: '#52525b', desc: 'Jovens (versículos sequenciais)' },
];

const NovoRecitativo = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [passo, setPasso] = useState(1);
  const [enviando, setEnviando] = useState(false);

  const [turma, setTurma] = useState(null);
  const [tipoPessoa, setTipoPessoa] = useState(null);

  const [ranking, setRanking] = useState([]);
  const [carregandoRanking, setCarregandoRanking] = useState(false);
  const [busca, setBusca] = useState('');
  const [selecionados, setSelecionados] = useState({});

  const [livros, setLivros] = useState([]);
  const [congregacoes, setCongregacoes] = useState([]);
  const [livro, setLivro] = useState('');
  const [capitulo, setCapitulo] = useState('');
  const [verso, setVerso] = useState('');
  const [localidade, setLocalidade] = useState('');
  const [nomeAuxiliar, setNomeAuxiliar] = useState('');
  const [observacoes, setObservacoes] = useState('');

  const [sequencia, setSequencia] = useState([]);
  const [carregandoSeq, setCarregandoSeq] = useState(false);

  // Pré-preenche o nome do auxiliar com o nome do usuário
  useEffect(() => {
    if (user?.nome && !nomeAuxiliar) setNomeAuxiliar(user.nome);
  }, [user, nomeAuxiliar]);

  // Carrega livros da Bíblia uma vez
  useEffect(() => {
    let mounted = true;
    bibliaService.listarLivros()
      .then((resp) => {
        if (mounted) setLivros(resp.data || []);
      })
      .catch((err) => {
        console.error('Erro ao carregar livros:', err);
        toast.error('Não foi possível carregar a lista de livros bíblicos');
      });
    return () => { mounted = false; };
  }, []);

  // Carrega congregações cadastradas para o select de localidade
  useEffect(() => {
    let mounted = true;
    congregacaoService.getAtivas()
      .then((resp) => {
        if (!mounted) return;
        const lista = resp?.data || [];
        const nomes = lista
          .map((c) => c.nome || c.nomeCompleto || c.nome_curto)
          .filter(Boolean);
        setCongregacoes(nomes);
      })
      .catch((err) => {
        console.error('Erro ao carregar congregações:', err);
      });
    return () => { mounted = false; };
  }, []);

  // Carrega ranking quando turma e tipo são definidos
  const carregarRanking = useCallback(async () => {
    if (!turma || !tipoPessoa) return;
    setCarregandoRanking(true);
    try {
      const resp = await recitativoService.getRanking(turma, tipoPessoa);
      setRanking(resp.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar lista da turma');
    } finally {
      setCarregandoRanking(false);
    }
  }, [turma, tipoPessoa]);

  useEffect(() => {
    if (passo === 3) carregarRanking();
  }, [passo, carregarRanking]);

  const livroAtual = useMemo(() => livros.find((l) => l.nome === livro), [livros, livro]);
  const maxCapitulo = livroAtual ? livroAtual.capitulos : null;
  const maxVerso = livroAtual && capitulo
    ? livroAtual.versosPorCapitulo[parseInt(capitulo, 10) - 1] || null
    : null;

  const selecionadosArr = useMemo(
    () => ranking.filter((m) => selecionados[m.id]).map((m) => m),
    [ranking, selecionados]
  );

  const totalSelecionados = selecionadosArr.length;

  // Calcula sequência sempre que livro/cap/verso/total mudam
  useEffect(() => {
    let cancelado = false;
    const calcular = async () => {
      if (!livro || !capitulo || !verso || totalSelecionados === 0 || !turma) {
        setSequencia([]);
        return;
      }
      try {
        setCarregandoSeq(true);
        const total = turma === 1 ? 1 : totalSelecionados;
        const resp = await bibliaService.gerarSequencia(livro, capitulo, verso, total);
        if (cancelado) return;
        // Para 1a turma todos recebem o mesmo verso
        if (turma === 1) {
          const seqUnica = resp.data?.[0];
          if (seqUnica) {
            setSequencia(Array.from({ length: totalSelecionados }, () => seqUnica));
          } else {
            setSequencia([]);
          }
        } else {
          setSequencia(resp.data || []);
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelado) setCarregandoSeq(false);
      }
    };
    calcular();
    return () => { cancelado = true; };
  }, [livro, capitulo, verso, totalSelecionados, turma]);

  const filtrados = useMemo(() => {
    if (!busca) return ranking;
    const q = busca.toLowerCase();
    return ranking.filter((m) =>
      (m.nome_completo || '').toLowerCase().includes(q)
    );
  }, [ranking, busca]);

  const toggleMembro = (id) => {
    setSelecionados((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const selecionarTodos = () => {
    const novo = {};
    filtrados.forEach((m) => { novo[m.id] = true; });
    setSelecionados((prev) => ({ ...prev, ...novo }));
  };
  const limparSelecao = () => setSelecionados({});

  const podeAvancar = () => {
    if (passo === 1) return !!turma;
    if (passo === 2) return !!tipoPessoa;
    if (passo === 3) return totalSelecionados > 0;
    if (passo === 4) {
      return !!livro && !!capitulo && !!verso && !!localidade && !!nomeAuxiliar
        && (turma === 1 || maxVerso == null || true); // verso já validado pelo input
    }
    return false;
  };

  const handleAvancar = () => {
    if (!podeAvancar()) return;
    setPasso((p) => Math.min(p + 1, 5));
  };

  const handleVoltar = () => {
    setPasso((p) => Math.max(p - 1, 1));
  };

  // Move um membro 1 posição para cima/baixo na lista de selecionados
  const moverSelecionado = (membroId, direcao) => {
    setRanking((prev) => {
      const idsSelec = prev.filter((m) => selecionados[m.id]).map((m) => m.id);
      const naoSelec = prev.filter((m) => !selecionados[m.id]);
      const idx = idsSelec.indexOf(membroId);
      if (idx === -1) return prev;
      const target = idx + direcao;
      if (target < 0 || target >= idsSelec.length) return prev;
      [idsSelec[idx], idsSelec[target]] = [idsSelec[target], idsSelec[idx]];
      const reordenados = idsSelec.map((id) => prev.find((m) => m.id === id)).filter(Boolean);
      return [...reordenados, ...naoSelec];
    });
  };

  const formatarRefSequencia = (idx) => {
    if (!sequencia[idx]) return '—';
    const s = sequencia[idx];
    return `${livro} ${s.capitulo}:${s.verso}`;
  };

  const handleEnviar = async () => {
    if (totalSelecionados === 0) return;
    setEnviando(true);
    try {
      const dados = {
        data_referencia: new Date().toISOString().slice(0, 10),
        localidade,
        turma,
        tipo_pessoa: tipoPessoa,
        livro,
        capitulo_inicial: parseInt(capitulo, 10),
        verso_inicial: parseInt(verso, 10),
        observacoes: observacoes || null,
        membros: selecionadosArr.map((m) => ({ irmandade_id: m.id })),
      };
      const resp = await recitativoService.criar(dados);
      if (resp?.success && resp.data) {
        toast.success('Recitativo criado! Gerando arquivo do WhatsApp…');

        // Reaproveita exportador atual com sequência já calculada
        const recitativoStr = `${livro} ${capitulo}:${verso}`;
        exportarContatosWhatsAppHTML(selecionadosArr, {
          nomeAuxiliar,
          localidade,
          recitativo: recitativoStr,
          titulo: `Recitativos ${TURMAS.find((t) => t.id === turma).label} - ${tipoPessoa === 'irmao' ? 'Irmãos' : 'Irmãs'}`,
          sequencia,
          crianca1aTurmaMesmoVerso: turma === 1,
        });

        navigate(`/recitativos/${resp.data.id}`);
      } else {
        toast.error('Erro ao criar recitativo');
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || 'Erro ao salvar recitativo');
    } finally {
      setEnviando(false);
    }
  };

  // ─────────────────────────────────────────────────────────
  // Renderização
  // ─────────────────────────────────────────────────────────
  return (
    <div className="rec-wizard">
      <header className="rec-wizard-top">
        <button
          type="button"
          className="rec-wizard-back"
          onClick={() => navigate('/recitativos')}
          aria-label="Voltar para listagem"
        >
          <FiArrowLeft />
        </button>
        <h1>Novo Recitativo</h1>
      </header>

      {/* Progresso */}
      <ol className="rec-stepper" aria-label="Etapas">
        {[1, 2, 3, 4].map((p) => (
          <li
            key={p}
            className={`rec-step ${p === passo ? 'atual' : ''} ${p < passo ? 'feito' : ''}`}
            aria-current={p === passo ? 'step' : undefined}
          >
            <span className="rec-step-num">{p < passo ? <FiCheck /> : p}</span>
            <span className="rec-step-label">
              {p === 1 && 'Turma'}
              {p === 2 && 'Gênero'}
              {p === 3 && 'Pessoas'}
              {p === 4 && 'Recitativo'}
            </span>
          </li>
        ))}
      </ol>

      <main className="rec-wizard-main">
        {passo === 1 && (
          <section className="rec-step-pane">
            <h2>Escolha a turma</h2>
            <p className="rec-step-help">Cada turma é definida pela faixa etária dos jovens/crianças.</p>
            <div className="rec-cards-3">
              {TURMAS.map((t) => {
                const Icon = t.icon;
                const ativo = turma === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    className={`rec-card-opt ${ativo ? 'ativo' : ''}`}
                    style={ativo ? { borderColor: t.cor, boxShadow: `0 0 0 3px ${t.cor}33` } : null}
                    onClick={() => setTurma(t.id)}
                  >
                    <span className="rec-card-icon" style={{ background: `${t.cor}1a`, color: t.cor }}>
                      <Icon />
                    </span>
                    <span className="rec-card-titulo">{t.label}</span>
                    <span className="rec-card-faixa">{t.faixa}</span>
                    <span className="rec-card-desc">{t.desc}</span>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {passo === 2 && (
          <section className="rec-step-pane">
            <h2>Escolha o gênero</h2>
            <p className="rec-step-help">A lista será filtrada para irmãos ou irmãs.</p>
            <div className="rec-cards-2">
              <button
                type="button"
                className={`rec-card-opt ${tipoPessoa === 'irmao' ? 'ativo' : ''}`}
                onClick={() => setTipoPessoa('irmao')}
              >
                <span className="rec-card-icon" style={{ background: '#dbeafe', color: '#1d4ed8' }}>
                  <FaMale />
                </span>
                <span className="rec-card-titulo">Irmãos</span>
                <span className="rec-card-desc">Lista de homens da {turma}ª turma</span>
              </button>
              <button
                type="button"
                className={`rec-card-opt ${tipoPessoa === 'irma' ? 'ativo' : ''}`}
                onClick={() => setTipoPessoa('irma')}
              >
                <span className="rec-card-icon" style={{ background: '#fce7f3', color: '#9d174d' }}>
                  <FaFemale />
                </span>
                <span className="rec-card-titulo">Irmãs</span>
                <span className="rec-card-desc">Lista de mulheres da {turma}ª turma</span>
              </button>
            </div>
          </section>
        )}

        {passo === 3 && (
          <section className="rec-step-pane">
            <header className="rec-step-titulo">
              <div>
                <h2>Selecione quem vai receber</h2>
                <p className="rec-step-help">
                  A lista está ordenada pela frequência. Os mais presentes aparecem primeiro.
                </p>
              </div>
              <div className="rec-step-acoes">
                <button type="button" className="rec-link" onClick={selecionarTodos}>
                  Selecionar todos
                </button>
                {totalSelecionados > 0 && (
                  <button type="button" className="rec-link rec-link-danger" onClick={limparSelecao}>
                    Limpar
                  </button>
                )}
              </div>
            </header>

            <div className="rec-search">
              <FiSearch />
              <input
                type="text"
                placeholder="Buscar pelo nome…"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            </div>

            {carregandoRanking ? (
              <div className="rec-loading">Carregando lista…</div>
            ) : filtrados.length === 0 ? (
              <div className="rec-vazio">
                <FiAlertCircle />
                <p>Nenhum membro encontrado para esta turma.</p>
              </div>
            ) : (
              <ul className="rec-membros">
                {filtrados.map((m, idx) => {
                  const ativo = !!selecionados[m.id];
                  const pct = m.estatisticas?.percentual;
                  const top10 = idx < 10 && totalSelecionados === 0;
                  return (
                    <li key={m.id} className={`rec-membro ${ativo ? 'ativo' : ''}`}>
                      <label className="rec-membro-row">
                        <input
                          type="checkbox"
                          checked={ativo}
                          onChange={() => toggleMembro(m.id)}
                          aria-label={`Selecionar ${m.nome_completo}`}
                        />
                        <span className="rec-membro-info">
                          <span className="rec-membro-nome">
                            {m.nome_completo}
                            {top10 && <span className="rec-top-pin">TOP {idx + 1}</span>}
                          </span>
                          <span className="rec-membro-meta">
                            {m.idade != null ? `${m.idade} anos · ` : ''}
                            {m.estatisticas?.total_recitativos > 0 ? (
                              <>
                                {m.estatisticas.presentes}/{m.estatisticas.presentes + m.estatisticas.ausentes} compareceram
                              </>
                            ) : 'Sem histórico ainda'}
                          </span>
                        </span>
                        <span className={`rec-membro-pct ${pct == null ? 'pct-neutro' : pct >= 70 ? 'pct-bom' : pct >= 40 ? 'pct-medio' : 'pct-baixo'}`}>
                          {pct != null ? `${pct}%` : '—'}
                        </span>
                      </label>
                      {ativo && (
                        <div className="rec-membro-mover">
                          <button type="button" onClick={() => moverSelecionado(m.id, -1)} aria-label="Mover para cima">▲</button>
                          <button type="button" onClick={() => moverSelecionado(m.id, +1)} aria-label="Mover para baixo">▼</button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {totalSelecionados > 0 && (
              <div className="rec-resumo">
                <FiUsers /> {totalSelecionados} selecionado(s)
              </div>
            )}
          </section>
        )}

        {passo === 4 && (
          <section className="rec-step-pane">
            <h2>Detalhes do recitativo</h2>
            <p className="rec-step-help">
              Informe o livro, capítulo e verso inicial. {turma === 1 ? '1ª turma recebe todos o mesmo verso.' : 'Os versos serão distribuídos em sequência (avança o capítulo se necessário).'}
            </p>

            <div className="rec-form">
              <div className="rec-form-row">
                <label className="rec-form-field">
                  <span>Livro</span>
                  <select
                    value={livro}
                    onChange={(e) => { setLivro(e.target.value); setCapitulo(''); setVerso(''); }}
                  >
                    <option value="">Selecione o livro…</option>
                    {livros.map((l) => <option key={l.nome} value={l.nome}>{l.nome}</option>)}
                  </select>
                </label>
              </div>

              <div className="rec-form-row rec-form-row-2">
                <label className="rec-form-field">
                  <span>Capítulo</span>
                  <input
                    type="number"
                    min={1}
                    max={maxCapitulo || undefined}
                    value={capitulo}
                    onChange={(e) => { setCapitulo(e.target.value); setVerso(''); }}
                    placeholder="Ex: 1"
                  />
                  {maxCapitulo && (
                    <small>Vai até o capítulo {maxCapitulo}</small>
                  )}
                </label>
                <label className="rec-form-field">
                  <span>Verso inicial</span>
                  <input
                    type="number"
                    min={1}
                    max={maxVerso || undefined}
                    value={verso}
                    onChange={(e) => setVerso(e.target.value)}
                    placeholder="Ex: 1"
                  />
                  {maxVerso && (
                    <small>Cap. {capitulo} tem {maxVerso} verso(s)</small>
                  )}
                </label>
              </div>

              <div className="rec-form-row rec-form-row-2">
                <label className="rec-form-field">
                  <span>Localidade da reunião</span>
                  <select
                    value={localidade}
                    onChange={(e) => setLocalidade(e.target.value)}
                  >
                    <option value="">Selecione a localidade…</option>
                    {congregacoes.map((nome) => (
                      <option key={nome} value={nome}>{nome}</option>
                    ))}
                  </select>
                </label>
                <label className="rec-form-field">
                  <span>Seu nome (auxiliar)</span>
                  <input
                    type="text"
                    value={nomeAuxiliar}
                    onChange={(e) => setNomeAuxiliar(e.target.value)}
                  />
                </label>
              </div>

              <label className="rec-form-field">
                <span>Observações (opcional)</span>
                <textarea
                  rows={2}
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  placeholder="Ex.: Reunião dedicada aos pais"
                />
              </label>
            </div>

            {/* Preview da sequência */}
            {totalSelecionados > 0 && livro && capitulo && verso && (
              <div className="rec-preview">
                <div className="rec-preview-titulo">
                  <FiBookOpen /> Pré-visualização ({totalSelecionados} pessoa{totalSelecionados !== 1 ? 's' : ''})
                  {carregandoSeq && <small>· calculando…</small>}
                </div>
                <ol className="rec-preview-lista">
                  {selecionadosArr.map((m, idx) => (
                    <li key={m.id}>
                      <span className="rec-preview-num">{idx + 1}.</span>
                      <span className="rec-preview-nome">{m.nome_completo}</span>
                      <span className="rec-preview-ref">{formatarRefSequencia(idx)}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </section>
        )}
      </main>

      {/* Rodapé do wizard */}
      <footer className="rec-wizard-footer">
        <button
          type="button"
          className="rec-btn-sec"
          onClick={handleVoltar}
          disabled={passo === 1 || enviando}
        >
          <FiChevronLeft /> Voltar
        </button>
        {passo < 4 && (
          <button
            type="button"
            className="rec-btn-pri"
            onClick={handleAvancar}
            disabled={!podeAvancar()}
          >
            Avançar <FiChevronRight />
          </button>
        )}
        {passo === 4 && (
          <button
            type="button"
            className="rec-btn-pri rec-btn-finalizar"
            onClick={handleEnviar}
            disabled={!podeAvancar() || enviando}
          >
            {enviando ? 'Salvando…' : (
              <><FaWhatsapp /> Gerar e enviar</>
            )}
          </button>
        )}
      </footer>
    </div>
  );
};

export default NovoRecitativo;
